import { useEffect, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

export const PostAssemblyCalibrationSection = ({
  postAssemblyCalibration,
  onCalibrationChange
}) => {
  const conductivity = postAssemblyCalibration?.conductivity || {};
  const savedScalarFactor = conductivity.manualScalarFactor;

  // Local text buffer so the box can hold an in-progress value (e.g. "0", "0.",
  // "0.0" while typing toward "0.01") without it being saved. Only a value that
  // parses to >= 0.01 gets committed via onCalibrationChange as it's typed.
  const [scalarFactorText, setScalarFactorText] = useState(
    savedScalarFactor != null ? String(savedScalarFactor) : ''
  );

  // Keep the buffer in sync when the saved value changes elsewhere (unit switch).
  useEffect(() => {
    setScalarFactorText(savedScalarFactor != null ? String(savedScalarFactor) : '');
  }, [savedScalarFactor]);

  if (!postAssemblyCalibration) return null;

  return (
    <Form.Group className="text-start mt-4 col-12" controlId="postAssemblyCalibration">
      <Form.Label>
        <strong>POST-ASSEMBLY CALIBRATION</strong>{' '}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="calibration-info-tooltip">
              Apply this calibration when the sensor's conductivity is losing its range.
              This calibration parameter multiplies the conductivity readings, except the
              starting threshold.
            </Tooltip>
          }
        >
          <span>
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#6c757d', cursor: 'help' }} />
          </span>
        </OverlayTrigger>
      </Form.Label>

      {/* Conductivity subsection */}
      <div className="ms-2 mt-2">
        <Form.Label className="text-muted"><strong>Conductivity</strong></Form.Label>

        {/* Enable toggle */}
        <div className="d-flex align-items-center gap-3 my-2">
          <Form.Check
            type="switch"
            id="calibration-enable-toggle"
            checked={conductivity.enable ?? false}
            onChange={() =>
              onCalibrationChange('conductivity.enable', !conductivity.enable)
            }
          />
          <span>{conductivity.enable ? 'Enabled' : 'Disabled'}</span>
        </div>

        {/* Manual scalar factor — only shown when enabled */}
        {conductivity.enable && (
          <div className="my-3">
            <Form.Label><strong>Manual Scalar Factor</strong></Form.Label>
            <Form.Control
              type="number"
              value={scalarFactorText}
              step="any"
              min="0.01"
              onChange={(e) => {
                const raw = e.target.value;
                setScalarFactorText(raw);
                const parsed = parseFloat(raw);
                // Only commit (and save) once it's a real number >= 0.01 — Parameters
                // divides by this value, so it must never be zero or vanishingly
                // small. Intermediate typing states like "0" or "0.0" stay in the
                // box uncommitted instead of being rejected outright.
                if (!isNaN(parsed) && parsed >= 0.01) {
                  onCalibrationChange('conductivity.manualScalarFactor', parsed);
                }
              }}
              onBlur={() => {
                // Leaving the field on an incomplete/invalid value snaps the
                // display back to the last actually-saved value.
                const parsed = parseFloat(scalarFactorText);
                if (isNaN(parsed) || parsed < 0.01) {
                  setScalarFactorText(savedScalarFactor != null ? String(savedScalarFactor) : '');
                }
              }}
              placeholder="Enter scalar factor"
            />
          </div>
        )}
      </div>
    </Form.Group>
  );
};
